import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styles from "./EquipmentsNew.module.scss";
import EquipmentsGrid from "@/pages/ConstructionSiteDetails/EquipmentsNew/EquipmentsGrid/EquipmentsGrid";
import {getProductsWithTelematics, getServiceRequestDefinitionId} from "@/services/ConstructionSiteService";
import {ProductWithTelematics} from "@/models/server/Models/Equipment/ProductWithTelematics";
import {ch} from "@renta-apps/athenaeum-react-common";
import UserContext from "@/models/server/UserContext";
import SubscribeToAlertsModal from "@/pages/FleetMonitoring/Modals/SubscribeToAlertsModal";
import RequestReturnModal from "@/pages/FleetMonitoring/Modals/RequestReturnModal/RequestReturnModal";
import {getItemKey, IDeletedAlarm, INewAlarm} from "@/pages/FleetMonitoring/Devices";
import BaseAlarmModel from "@/models/BaseAlarmModel";
import CreateConstructionSiteServiceRequestRequest from "@/models/server/Requests/CreateConstructionSiteServiceRequestRequest";
import {ServiceType} from "@/models/Enums";
import {Modal} from "@renta-apps/athenaeum-react-components";
import ServiceRequestModal from "@/pages/ConstructionSiteDetails/Equipments/ServiceRequestModal";
import {ConstructionSiteModel} from "@/models/server/ConstructionSiteModel";
import {Pagination} from "@renta-apps/renta-react-components";
import GetReportModal from "@/pages/ConstructionSiteDetails/EquipmentsNew/GetReportModal/GetReportModal";
import FiltersAndSort from "@/pages/ConstructionSiteDetails/EquipmentsNew/FIltersAndSort/FiltersAndSort";
import {SortDirection} from "@renta-apps/athenaeum-toolkit";
import UserInteractionDataStorage, {DataStorageType} from "@/providers/UserInteractionDataStorage";
import isEqual from "lodash.isequal";
import SelectedFilter from "@/models/server/Requests/SelectedFilter";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import Localizer from "@/localization/Localizer";

interface EquipmentsNewProps {
    urlFilters?: EquipmentsUrlParams;
    constructionSiteId: string;
    constructionSite: ConstructionSiteModel | null;

    serviceCreated?(): Promise<void>;

    onEquipmentFiltersChange?(filters: EquipmentsFiltersSortAndPagination): Promise<void>;
}

export interface EquipmentsUrlParams {
    sortBy?: string;
    sortOrder?: string;
    pageNumber?: number;
    pageSize?: number;
    deviceNames?: string[];
    productGroups?: SelectedFilter[];
    statuses?: SelectedFilter[];
}

export interface EquipmentsFiltersSortAndPagination {
    sortBy: string;
    sortOrder: string;
    pageNumber: number;
    pageSize: number;
    deviceNames: string[];
    productGroups: SelectedFilter[];
    statuses: SelectedFilter[];
}

const defaultStatus = RentaEasyConstants.ConstructionSiteEquipmentConstants.statusInRent;
const defaultSortBy = "CategoryName";
const defaultSortOrder = "Asc";
const defaultPageNumber = 1;
const defaultPageSize = 25;

const EquipmentsNew: React.FC<EquipmentsNewProps> = (props) => {
    const {userRoleOrganizationId, userRoleContractId, userRoleConstructionSiteId, isEasyPlusUser, canSelectChargingRequest, selectedRoleId, user} = useMemo(() => {
        const userContext = (ch.getContext() as UserContext);
        return {
            userRoleOrganizationId: userContext.selectedRole?.organization?.id ?? null,
            userRoleContractId: userContext.selectedRole?.contractId ?? null,
            userRoleConstructionSiteId: userContext.selectedRole?.constructionSiteId ?? null,
            isEasyPlusUser: userContext.isEasyPlusUser,
            canSelectChargingRequest: !ch.isNorway,
            selectedRoleId: userContext.selectedRoleId,
            user: userContext.user,
        };
    }, []);

    const USER_DATA_STORAGE_KEY = useMemo(() => {
        return `EquipmentsFilters-${selectedRoleId}-${props.constructionSiteId}`;
    }, []);

    const createInitialFilters = useCallback((): EquipmentsFiltersSortAndPagination => {
        let initialParams = props.urlFilters;
        if (!initialParams || Object.values(initialParams).every(value => value === undefined)) {
            initialParams = UserInteractionDataStorage.get(USER_DATA_STORAGE_KEY, {}, DataStorageType.Page);
        }

        return {
            sortBy: initialParams?.sortBy ?? defaultSortBy,
            sortOrder: initialParams?.sortOrder ?? defaultSortOrder,
            deviceNames: initialParams?.deviceNames ?? [],
            pageNumber: initialParams?.pageNumber === undefined ? defaultPageNumber : Number(initialParams!.pageNumber),
            pageSize: initialParams?.pageSize === undefined ? defaultPageSize : Number(initialParams!.pageSize),
            productGroups: initialParams?.productGroups ?? [],
            statuses: initialParams?.statuses ?? [{id: defaultStatus, name: Localizer.constructionSiteDetailsEquipmentTabStatusInRent}],
        };
    }, [props.urlFilters]);

    const dataGridRef = useRef<HTMLDivElement>(null);
    const [equipments, setEquipments] = useState<ProductWithTelematics[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubscribeToAlertsModalOpen, setIsSubscribeToAlertsModalOpen] = useState(false);
    const [isReturnRequestModalOpen, setIsReturnRequestModalOpen] = useState(false);
    const [isServiceRequestModalOpen, setIsServiceRequestModalOpen] = useState(false);
    const [isGetReportModalOpen, setIsGetReportModalOpen] = useState(false);
    const [selectedSingleDevice, setSelectedSingleDevice] = useState<ProductWithTelematics | undefined>(undefined);
    const [serviceRequest, setServiceRequest] = useState<CreateConstructionSiteServiceRequestRequest | undefined>(undefined);
    const [selectedDevices, setSelectedDevices] = useState<ProductWithTelematics[]>([]);
    const [serviceTypeToIdMap, setServiceTypeToIdMap] = useState<Record<number, string>>({
        [ServiceType.Refuel]: '',
        [ServiceType.Maintenance]: '',
        [ServiceType.Charging]: '',
    });
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [scrollToTop, setScrollToTop] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [filtersSortAndPagination, setFiltersSortAndPagination] = useState<EquipmentsFiltersSortAndPagination>(createInitialFilters);

    const serviceRequestModalRef = useRef<Modal>(null);

    useEffect(() => {
        loadEquipments().catch();
    }, [filtersSortAndPagination]);

    useEffect(() => {
        const newFilters = createInitialFilters();
        if (!isEqual(newFilters, filtersSortAndPagination)) {
            setFiltersSortAndPagination(createInitialFilters);
        }
    }, [createInitialFilters]);

    const saveUserSelection = async (filters: EquipmentsFiltersSortAndPagination) => {
        await props.onEquipmentFiltersChange?.(filters);

        UserInteractionDataStorage.set(USER_DATA_STORAGE_KEY, filters, DataStorageType.Page);
    };

    const loadEquipments = async () => {
        setIsLoading(true);

        try {
            if (!initialLoad) {
                await saveUserSelection(filtersSortAndPagination);
            }
            const {sortBy, sortOrder, pageNumber, pageSize, deviceNames, productGroups, statuses} = filtersSortAndPagination;
            const sortDirectionEnum = SortDirection[sortOrder as keyof typeof SortDirection];
            const data = await getProductsWithTelematics(
                props.constructionSiteId,
                pageNumber,
                pageSize,
                userRoleOrganizationId,
                sortBy,
                sortDirectionEnum,
                userRoleContractId,
                userRoleConstructionSiteId,
                deviceNames,
                productGroups,
                statuses,
            );

            setEquipments(data.items);
            setTotalItemCount(data.totalItemCount);
        } catch (error) {
            console.error('Error loading construction site equipment: ', error);
        } finally {
            setIsLoading(false);
            setInitialLoad(false);
            if (scrollToTop && dataGridRef.current) {
                setScrollToTop(false);
                // 45 - height of the top nav bar
                window.scrollTo({top: dataGridRef.current.offsetTop - 45, behavior: 'smooth'});
            }
        }
    };

    useEffect(() => {
        if (!isServiceRequestModalOpen && !serviceRequestModalRef?.current) {
            return;
        }

        serviceRequestModalRef?.current?.openAsync();
    }, [isServiceRequestModalOpen, serviceRequestModalRef]);

    const handleOnSubscribeButtonClick = (device: ProductWithTelematics): void => {
        setSelectedSingleDevice(device);
        setIsSubscribeToAlertsModalOpen(true);
    };

    const handleOnReturnButtonClick = (device: ProductWithTelematics): void => {
        setSelectedSingleDevice(device);
        setIsReturnRequestModalOpen(true);
    };

    const getServiceRequestDefinitionIdAsync = async (serviceType: ServiceType): Promise<string> => {
        let serviceTypeId = serviceTypeToIdMap[serviceType];
        if (!serviceTypeId) {
            serviceTypeId = await getServiceRequestDefinitionId(serviceType);
            setServiceTypeToIdMap(prev => ({...prev, [serviceType]: serviceTypeId}));
        }

        return serviceTypeId;
    };

    const handleOnServiceRequestButtonClick = async (device: ProductWithTelematics, serviceType: ServiceType): Promise<void> => {
        const serviceRequestDefinitionId: string = await getServiceRequestDefinitionIdAsync(serviceType);
        setServiceRequest({
            serviceRequestDefinitionId: serviceRequestDefinitionId,
            type: serviceType,
            message: null,
            constructionSiteId: props.constructionSiteId,
            deviceExternalId: device.inventoryItemDisplayNumber,
            depotExternalId: device.location,
            deviceName: device.deviceName,
            images: null,
        });
        setIsServiceRequestModalOpen(true);
    };

    const updateDevicesSubscriptions = (newAlarms: INewAlarm[], deletedAlarms: IDeletedAlarm[]) => {
        setEquipments(prevEquipments => {
            return prevEquipments.map(equipment => {
                if (!equipment.supportsAlerts) {
                    return equipment;
                }
                const newAlerts = newAlarms.filter(x => x.deviceId === equipment.rentaId).map((x: INewAlarm): BaseAlarmModel => ({
                    id: x.configuredAlert.id,
                    alarmData: x.configuredAlert.alarmData,
                    alarmType: x.configuredAlert.alarmType,
                }));
                const removedAlertIds = deletedAlarms.filter(x => x.deviceId === equipment.rentaId).map(x => x.alarmId);
                if (!newAlerts.length && !removedAlertIds.length) {
                    return equipment;
                }

                return {
                    ...equipment,
                    configuredAlerts: equipment.configuredAlerts.filter(x => !removedAlertIds.includes(x.id)).concat(newAlerts),
                };
            });
        });
    };

    const handleRequestReturnModalClose = (returnedItems: string[]) => {
        setEquipments(prevEquipments => {
            return prevEquipments.map(equipment => {
                if (returnedItems.includes(getItemKey(equipment))) {
                    return {
                        ...equipment,
                        isReturnRequested: true,
                    };
                }
                return equipment;
            });
        });
        setSelectedSingleDevice(undefined);
        setIsReturnRequestModalOpen(false);
    };

    const handleSubscribeToAlertsModalClose = () => {
        setSelectedSingleDevice(undefined);
        setIsSubscribeToAlertsModalOpen(false);
    };

    const handleServiceRequestModalClose = () => {
        setServiceRequest(undefined);
        setIsServiceRequestModalOpen(false);
    };

    const handleGetReportModalOpen = () => {
        setIsGetReportModalOpen(true);
    };

    const handleGetReportModalClose = () => {
        setIsGetReportModalOpen(false);
    };

    const handleFiltersSortAndPaginationChange = (newFilters: Partial<EquipmentsFiltersSortAndPagination>, scrollToTop: boolean, resetPage: boolean) => {
        setScrollToTop(scrollToTop);
        setFiltersSortAndPagination(prev => ({
            ...prev,
            ...(resetPage ? {pageNumber: 1} : {}),
            ...newFilters,
        }));
    };

    return (
        <div className={styles.container}>
            <FiltersAndSort params={filtersSortAndPagination}
                            onParamValueChange={(change: Partial<EquipmentsFiltersSortAndPagination>) => handleFiltersSortAndPaginationChange(change, false, true)}
                            onGetReportModalOpen={handleGetReportModalOpen}
            />
            <EquipmentsGrid constructionSiteId={props.constructionSiteId}
                            isLoading={isLoading}
                            data={equipments}
                            canSelectChargingRequest={canSelectChargingRequest}
                            isEasyPlusUser={isEasyPlusUser}
                            onSubscribeButtonClick={handleOnSubscribeButtonClick}
                            onReturnButtonClick={handleOnReturnButtonClick}
                            onServiceRequestButtonClick={handleOnServiceRequestButtonClick}
                            gridRef={dataGridRef}
            />

            <Pagination pageNumber={filtersSortAndPagination.pageNumber}
                        pageSize={filtersSortAndPagination.pageSize}
                        totalItemCount={totalItemCount}
                        onPageNumberChange={(pageNumber: number) => handleFiltersSortAndPaginationChange({pageNumber}, true, false)}
                        onPageSizeChange={(pageSize: number) => handleFiltersSortAndPaginationChange({pageSize}, true, false)}
                        data-cy="equipments-grid-pagination"
            />

            <SubscribeToAlertsModal selectedDevices={selectedSingleDevice ? [selectedSingleDevice] : selectedDevices}
                                    updateDevicesSubscriptions={updateDevicesSubscriptions}
                                    isOpen={isSubscribeToAlertsModalOpen}
                                    onClose={handleSubscribeToAlertsModalClose}
            />

            <RequestReturnModal isOpen={isReturnRequestModalOpen}
                                onClose={handleRequestReturnModalClose}
                                isEasyPlusUser={isEasyPlusUser}
                                constructionSiteId={props.constructionSiteId}
                                customerId={props.constructionSite?.ownerId}
                                selectedDevices={selectedSingleDevice ? [selectedSingleDevice] : selectedDevices.filter(device => !device.isReturnRequested)}
            />

            {(isServiceRequestModalOpen && !!serviceRequest && !!props.constructionSite) && (
                <ServiceRequestModal modalRef={serviceRequestModalRef}
                                     serviceRequest={serviceRequest!}
                                     serviceCreated={props.serviceCreated}
                                     constructionSite={props.constructionSite!}
                                     onClose={handleServiceRequestModalClose}
                                     user={user}
                />
            )}

            <GetReportModal isOpen={isGetReportModalOpen}
                            onClose={handleGetReportModalClose}
                            constructionSite={props.constructionSite}
                            data={equipments}/>
        </div>
    );
};

export default EquipmentsNew;